// Global JS - site chrome only: header cart count, category mega menu, mobile nav drawer and the
// cookie-consent banner (all rendered on every page by layout/theme.liquid and the header/footer
// groups). There is no cart drawer: add-to-cart forms post to /cart/add with return_to=/cart and
// the cart icon links to /cart. Page behaviour lives in its own files, loaded by the sections
// that need them (collection.js, product.js, cart.js).
(function () {
  window.SlowBurnGoods = window.SlowBurnGoods || {};

  // Header cart count (sections/header.liquid): hidden at 0. Page scripts that change the cart via
  // AJAX call window.SlowBurnGoods.syncCartCount(count) - or dispatch "cart:updated" to refetch it.
  function syncCartCount(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll("[data-cart-count]").forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    document.querySelectorAll("[data-cart-link]").forEach((link) => {
      const label = link.getAttribute("aria-label");
      if (label) link.setAttribute("aria-label", label.replace(/\(\d+/, `(${count}`));
    });
  }
  async function refreshCartCount() {
    try {
      const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const cart = await response.json();
      syncCartCount(cart.item_count);
    } catch {
      // Non-fatal - the count is correct again on the next page load.
    }
  }
  window.SlowBurnGoods.syncCartCount = syncCartCount;
  document.addEventListener("cart:updated", refreshCartCount);

  // Focusable elements inside chrome containers (drawer, mega menu); also used by page scripts.
  const CHROME_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  // Keeps Tab / Shift+Tab inside an open modal container (nav drawer).
  function chromeTrapFocus(event, container) {
    const items = [...container.querySelectorAll(CHROME_FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  window.SlowBurnGoods.trapFocus = chromeTrapFocus;

  // Mobile nav drawer (snippets/mobile-menu.liquid) - a modal dialog: focus moves in and stays
  // inside, the page behind stops scrolling, Esc / overlay / close button close it and focus
  // returns to the hamburger.
  function openNavDrawer() {
    const overlay = document.querySelector("[data-nav-overlay]");
    if (!overlay) return;
    overlay.classList.add("is-open");
    document.documentElement.classList.add("is-scroll-locked");
    document.querySelectorAll("[data-menu-open]").forEach((button) => button.setAttribute("aria-expanded", "true"));
    overlay.querySelector("[data-nav-drawer]")?.focus();
  }
  function closeNavDrawer(returnFocus = true) {
    const overlay = document.querySelector("[data-nav-overlay]");
    if (!overlay?.classList.contains("is-open")) return;
    overlay.classList.remove("is-open");
    document.documentElement.classList.remove("is-scroll-locked");
    document.querySelectorAll("[data-menu-open]").forEach((button) => button.setAttribute("aria-expanded", "false"));
    if (returnFocus) document.querySelector("[data-menu-open]")?.focus();
  }
  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-menu-open]")) {
      openNavDrawer();
      return;
    }
    if (event.target.closest("[data-nav-close]") || event.target.matches("[data-nav-overlay]")) closeNavDrawer();
  });
  document.addEventListener("keydown", (event) => {
    const overlay = document.querySelector("[data-nav-overlay].is-open");
    if (!overlay) return;
    if (event.key === "Escape") closeNavDrawer();
    else if (event.key === "Tab") chromeTrapFocus(event, overlay.querySelector("[data-nav-drawer]"));
  });
  // The drawer only exists below 1200px; don't leave the page scroll-locked after a resize.
  window.matchMedia("(min-width: 1200px)").addEventListener("change", (event) => {
    if (event.matches) closeNavDrawer(false);
  });

  // Category mega menu (sections/header.liquid + snippets/mega-menu-panel.liquid, desktop ≥1200).
  // Each category link with a panel ([data-mega-root]) stays a normal link; its panel opens on
  // mouse hover (short intent delay) and when keyboard focus enters the item, closes shortly after
  // the pointer leaves, when focus leaves the item, on Esc (focus back to the link) or on a click
  // outside. Only one panel is open at a time. aria-expanded on the link mirrors the state.
  const megaRoots = new Set();
  function initMegaMenu(root) {
    const trigger = root.querySelector("[data-mega-trigger]");
    const menu = root.querySelector("[data-mega-menu]");
    if (!trigger || !menu || megaRoots.has(root)) return;
    megaRoots.add(root);
    const desktop = window.matchMedia("(min-width: 1200px)");
    let openTimer;
    let closeTimer;

    const isOpen = () => menu.classList.contains("is-open");
    function open() {
      clearTimeout(closeTimer);
      if (isOpen() || !desktop.matches) return;
      megaRoots.forEach((other) => other !== root && other.dispatchEvent(new CustomEvent("mega:close")));
      menu.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
    }
    function close(returnFocus = false) {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (!isOpen()) return;
      menu.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      if (returnFocus) trigger.focus();
    }
    root.addEventListener("mega:close", () => close());

    root.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "mouse") return;
      clearTimeout(closeTimer);
      openTimer = setTimeout(open, 120);
    });
    root.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "mouse") return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(close, 200);
    });
    root.addEventListener("focusin", (event) => {
      if (event.target === trigger && trigger.matches(":focus-visible")) open();
    });
    root.addEventListener("focusout", (event) => {
      if (!root.contains(event.relatedTarget)) close();
    });
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && isOpen()) {
        event.stopPropagation();
        close(true);
      } else if (event.key === "ArrowDown" && event.target === trigger) {
        event.preventDefault();
        open();
        menu.querySelector(CHROME_FOCUSABLE)?.focus();
      }
    });
    document.addEventListener("click", (event) => {
      if (!root.contains(event.target)) close();
    });
    desktop.addEventListener("change", () => close());

    // Theme editor: selecting a mega-menu block opens its panel.
    document.addEventListener("shopify:block:select", (event) => {
      if (menu.contains(event.target)) {
        menu.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }
    });
    document.addEventListener("shopify:block:deselect", (event) => {
      if (menu.contains(event.target)) close();
    });
  }
  document.querySelectorAll("[data-mega-root]").forEach(initMegaMenu);

  // Theme editor re-renders a section on every settings change: re-wire the chrome inside it.
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-mega-root]").forEach(initMegaMenu);
  });

  // Predictive search hook: the header's search form carries [data-predictive-search] and its
  // input [data-predictive-search-input]; assets/predictive-search.js attaches its results
  // dropdown to those. Without it the form is a plain GET to /search.

  // Header search clear button (replaces the browser's own search "×"): visible while the field
  // has text; clearing fires an `input` event so the predictive dropdown closes with it.
  document.querySelectorAll("[data-predictive-search]").forEach((form) => {
    const input = form.querySelector("[data-predictive-search-input]");
    const clear = form.querySelector("[data-search-clear]");
    if (!input || !clear) return;
    const sync = () => {
      clear.hidden = input.value === "";
    };
    input.addEventListener("input", sync);
    clear.addEventListener("click", () => {
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.focus();
    });
    sync();
  });

  // Cookie consent (snippets/cookie-consent.liquid) - drives Shopify's low-level Customer
  // Privacy API directly (window.Shopify.customerPrivacy) instead of the hosted
  // `privacyBanner.loadBanner()` UI, so this theme renders its own styled banner while still
  // writing real consent state (the same state Shopify-managed pixels/checkout read from).
  // Note: this replaces the banner's UI, not Shopify's own default banner's *appearance setting*
  // - that toggle lives in Shopify admin under Settings > Customer Privacy > Cookie banner and
  // is store configuration, not something theme code can turn off.
  (function initCookieConsent() {
    const banner = document.querySelector("[data-cookie-consent]");
    const modal = document.querySelector("[data-cookie-preferences]");
    if (!banner || !modal || !window.Shopify?.loadFeatures) return;

    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (error) return; // non-fatal - no banner shown if the API itself fails to load
      // shouldShowBanner() is a REGION check only ("is this visitor somewhere a banner is
      // required"), not a "has this visitor already decided" check - Shopify's own docs say so
      // explicitly. Calling it alone re-showed the banner on every load for any EU visitor even
      // after they'd already accepted/rejected (confirmed live: currentVisitorConsent() had real
      // "yes" values right after Accept, yet shouldShowBanner() still returned true on the next
      // page load). A decision only exists once at least one signal is no longer the unset ""
      // value - Accept/Reject/Save here always write all four signals together, so checking any
      // one of them is enough to know a decision was already made.
      const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
      const alreadyDecided = Object.values(consent).some((value) => value === "yes" || value === "no");
      if (window.Shopify.customerPrivacy.shouldShowBanner() && !alreadyDecided) banner.hidden = false;
      // Google Tag Manager consent (snippets/gtm-head.liquid). Regions that don't require a banner
      // have no consent gate, so everything is granted there; otherwise mirror the stored choice.
      if (!window.Shopify.customerPrivacy.shouldShowBanner()) {
        syncGtmConsent({ analytics: true, marketing: true, preferences: true });
      } else if (alreadyDecided) {
        syncGtmConsent({
          analytics: consent.analytics === "yes",
          marketing: consent.marketing === "yes",
          preferences: consent.preferences === "yes",
        });
      }
    });

    // No-op when GTM is not configured (sbgUpdateGtmConsent is only defined by snippets/gtm-head.liquid).
    function syncGtmConsent(values) {
      if (typeof window.sbgUpdateGtmConsent === "function") window.sbgUpdateGtmConsent(values);
    }

    function hideAll() {
      banner.hidden = true;
      modal.hidden = true;
    }

    // sale_of_data has no dedicated UI toggle in the preferences panel (kept simple, per design)
    // - tied to the marketing choice, the common mapping for this signal.
    // setTrackingConsent's callback receives an error on failure (confirmed live: it does NOT
    // call back with no arguments on success only - silently treating every callback as success
    // hid the banner even when the write itself had failed, e.g. a "Failed to fetch" on the
    // underlying Storefront API call), so only hide once the write is actually confirmed.
    function setConsent(values, callback) {
      window.Shopify.customerPrivacy.setTrackingConsent(
        { sale_of_data: values.marketing, ...values },
        (error) => {
          if (error) return; // non-fatal - banner/modal stay open so the shopper can retry
          syncGtmConsent(values);
          (callback || hideAll)();
        },
      );
    }

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-cookie-accept]")) {
        setConsent({ marketing: true, analytics: true, preferences: true });
        return;
      }
      if (event.target.closest("[data-cookie-reject]")) {
        setConsent({ marketing: false, analytics: false, preferences: false });
        return;
      }
      if (event.target.closest("[data-cookie-manage]")) {
        const current = window.Shopify.customerPrivacy.currentVisitorConsent();
        modal.querySelectorAll("[data-cookie-pref]").forEach((input) => {
          input.checked = current[input.dataset.cookiePref] === "yes";
        });
        banner.hidden = true;
        modal.hidden = false;
        return;
      }
      if (event.target.closest("[data-cookie-save]")) {
        const values = {};
        modal.querySelectorAll("[data-cookie-pref]").forEach((input) => {
          values[input.dataset.cookiePref] = input.checked;
        });
        setConsent(values);
        return;
      }
      if (event.target.closest("[data-cookie-preferences-close]") || event.target.matches("[data-cookie-preferences]")) {
        // Closed without saving - no decision was made, so the banner (not the modal) still applies.
        modal.hidden = true;
        banner.hidden = false;
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        modal.hidden = true;
        banner.hidden = false;
      }
    });
  })();

  // Language switcher (snippets/language-switcher.liquid): switch as soon as a language is picked.
  document.addEventListener("change", (event) => {
    const select = event.target.closest && event.target.closest("[data-language-select]");
    if (select && select.form) select.form.submit();
  });

})();
